import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { db } from '../firebase';
import { 
  collection, query, orderBy, onSnapshot, 
  addDoc, updateDoc, doc, writeBatch, serverTimestamp 
} from 'firebase/firestore';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    // Subscribe to user's notifications collection
    const q = query(
      collection(db, `users/${user.id}/notifications`), 
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const notifs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        // Handle Firestore timestamps vs local dates
        createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : new Date(doc.data().createdAt)
      }));
      
      setNotifications(notifs);
      setUnreadCount(notifs.filter(n => !n.read).length);
    });

    return () => unsubscribe();
  }, [user]);

  const addNotification = async (notification) => {
    // This function is typically called by other components (e.g. when assigning a task)
    // It should ideally write to the RECIPIENT'S notification collection.
    // If notification.userId is provided, send to that user. Otherwise send to current user (for testing/local feedback).
    
    const targetUserId = notification.userId || user?.id;
    if (!targetUserId) return;

    await addDoc(collection(db, `users/${targetUserId}/notifications`), {
      ...notification,
      read: false,
      createdAt: new Date().toISOString() // Store as ISO string or serverTimestamp
    });
  };

  const markAsRead = async (notificationId) => {
    if (!user) return;
    const notifRef = doc(db, `users/${user.id}/notifications`, notificationId);
    await updateDoc(notifRef, { read: true });
  };

  const markAllAsRead = async () => {
    if (!user || notifications.length === 0) return;
    
    const batch = writeBatch(db);
    notifications.forEach(n => {
      if (!n.read) {
        const ref = doc(db, `users/${user.id}/notifications`, n.id);
        batch.update(ref, { read: true });
      }
    });
    
    await batch.commit();
  };

  const value = {
    notifications,
    unreadCount,
    addNotification,
    markAsRead,
    markAllAsRead,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};